import React, { useState, useEffect } from 'react';
import { Button, Input, Rate, Avatar, Empty, message } from 'antd';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { isAuthenticated } from '../../../services/auth';
import { createComment, getComments, deleteComment, updateComment } from '../../../services/comment';
import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

const API = (process.env.REACT_APP_API).replace(/\/api$/, '');

const ProductComments = ({ productId }) => {
  const [comments, setComments] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const [editingComment, setEditingComment] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editText, setEditText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const { user, token } = isAuthenticated();

  const loadComments = async () => {
    try {
      setLoadingComments(true);
      const response = await getComments(productId);
      setComments(response.data);
    } catch (error) {
      console.error('Erro ao carregar comentários:', error);
      message.error('Erro ao carregar comentários');
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    if (productId) {
      loadComments();
    }
  }, [productId]);

  const handleSubmit = async () => {
    if (!user || !token) {
      message.error('Você precisa estar logado para comentar');
      return;
    }

    if (!rating || rating < 1 || rating > 5) {
      message.error('Selecione uma classificação válida');
      return;
    }

    if (!comment.trim()) {
      message.error('Digite um comentário');
      return;
    }

    setLoading(true);
    try {
      const response = await createComment(productId, {
        rating,
        comment: comment.trim(),
      });

      setComments([response.data, ...comments]);
      setComment('');
      setRating(0);
      message.success('Comentário enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar comentário:', error);
      message.error(error.response?.data?.error || 'Erro ao enviar comentário');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (comment) => {
    setEditingComment(comment._id);
    setEditRating(comment.rating);
    setEditText(comment.comment);
  };

  const handleSaveEdit = async () => {
    if (!editRating || editRating < 1 || editRating > 5) {
      message.error('Selecione uma classificação válida');
      return;
    }

    if (!editText.trim()) {
      message.error('Digite um comentário');
      return;
    }

    try {
      const response = await updateComment(editingComment, {
        rating: editRating,
        comment: editText.trim(),
      });

      setComments(comments.map(c => c._id === editingComment ? response.data : c));
      setEditingComment(null);
      setEditRating(0);
      setEditText('');
      message.success('Comentário editado com sucesso!');
    } catch (error) {
      console.error('Erro ao editar comentário:', error);
      message.error('Erro ao editar comentário');
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditRating(0);
    setEditText('');
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteComment(commentId);
      setComments(comments.filter(c => c._id !== commentId));
      message.success('Comentário deletado com sucesso');
    } catch (error) {
      console.error('Erro ao deletar comentário:', error);
      message.error('Erro ao deletar comentário');
    }
    handleCloseMenu();
  };

  const handleOpenMenu = (event, comment) => {
    setAnchorEl(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedComment(null);
  };

  const handleMenuEdit = () => {
    if (selectedComment) {
      handleEdit(selectedComment);
    }
    handleCloseMenu();
  };

  const handleMenuDelete = () => {
    if (selectedComment) {
      handleDelete(selectedComment._id);
    }
  };

  return (
    <div className='product-comments-container'>
      <h3>Comentários e Avaliações</h3>

      {user && token ? (
        <div className='product-comments-form'>
          <div className='comments-form-group'>
            <label>Sua Avaliação</label>
            <Rate
              value={rating}
              onChange={setRating}
              allowHalf={false}
              className='comments-rate'
            />
          </div>

          <div className='comments-form-group'>
            <label>Seu Comentário</label>
            <Input.TextArea
              rows={4}
              placeholder='Compartilhe sua experiência com este produto...'
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              showCount
            />
          </div>

          <Button
            type='primary'
            size='large'
            onClick={handleSubmit}
            loading={loading}
            className='comments-submit-btn'
          >
            Enviar Comentário
          </Button>
        </div>
      ) : (
        <div className='comments-login-message'>
          <p>Faça login para deixar um comentário</p>
        </div>
      )}

      <div className='product-comments-list'>
        {loadingComments ? (
          <div>Carregando comentários...</div>
        ) : comments && comments.length > 0 ? (
          comments.map((c) => (
            <div key={c._id} className='comment-item'>
              <div className='comment-header'>
                <Avatar
                  src={c.author?.photo ? `${API}${c.author.photo}` : null}
                  size={40}
                  style={{ backgroundColor: '#ffffff', color: '#595959' }}
                >
                  {c.author?.name?.[0]?.toUpperCase()}
                </Avatar>
                <div className='comment-info'>
                  <h5>{c.author?.name}</h5>
                 
                </div>
                
                {user && c.author?._id === user._id && (
                  <>
                    <IconButton
                      onClick={(e) => handleOpenMenu(e, c)}
                      size='small'
                      sx={{ marginLeft: 'auto' }}
                    >
                      <MoreVertIcon fontSize='small' />
                    </IconButton>
                  </>
                )}
              </div>
               <div className='comment-meta'>
                    <Rate value={c.rating} disabled className='comment-rate' />
                    <span className='comment-date'>
                      {moment(c.createdAt).fromNow()}
                      {c.edited && ' (editado)'}
                    </span>
                  </div>
              {editingComment === c._id ? (
                <div className='comment-edit'>
                  <Rate
                    value={editRating}
                    onChange={setEditRating}
                    allowHalf={false}
                    className='comments-rate'
                  />
                  <Input.TextArea
                    rows={3}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    maxLength={500}
                    showCount
                  />
                  <div className='edit-actions'>
                    <Button size='small' onClick={handleSaveEdit} type='primary'>
                      Salvar
                    </Button>
                    <Button size='small' onClick={handleCancelEdit}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <p className='comment-text'>{c.comment}</p>
              )}
            </div>
          ))
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px', marginBottom: '40px' }}>
            <Empty description='Nenhum comentário ainda' />
          </div>
        )}
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleMenuEdit}>Editar</MenuItem>
        <MenuItem onClick={handleMenuDelete} sx={{ color: '#f5222d' }}>
          Deletar
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ProductComments;
